// Shared error-detail formatting. FastAPI's default RequestValidationError
// handler returns `detail` as an ARRAY OF OBJECTS ({loc, msg, type}), not a
// string, for body-validation failures (see backend/main.py's custom
// RequestValidationError handler, ~line 359-410). Rendering that array
// directly as a JSX child (e.g. `{error}` inside <Alert>/<Typography>)
// throws "Objects are not valid as a React child" and crashes the whole
// view — not just an ugly message. Every call site that reads
// err.response?.data?.detail and displays it as a string must go through
// this helper instead of using the raw value directly.
export function formatErrorDetail(detail, fallback) {
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => (item && typeof item.msg === "string" ? item.msg : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
}

export function extractErrorDetail(err, fallback) {
  return formatErrorDetail(err?.response?.data?.detail, fallback);
}

// 2026-10-23 addition (clinician invite-only build, Piece 4 UI-review
// fix) — ports the backend's own _is_valid_npi check-digit logic
// (labariumaiportal/backend/main.py, "80840" prefix + Luhn algorithm per
// the CMS NPI assignment spec) to the client, so a malformed-but-
// 10-digit NPI (the exact phone-number-shaped bug this backend check was
// added to catch) fails fast before triggering the NPPES confirm-dialog
// round trip, instead of only failing after it.
export function isValidNpi(npi) {
  const digits = (npi || "").replace(/\D/g, "");
  if (!/^\d{10}$/.test(digits)) return false;
  const payload = "80840" + digits.slice(0, 9);
  let total = 0;
  const reversed = payload.split("").reverse();
  for (let i = 0; i < reversed.length; i++) {
    let d = parseInt(reversed[i], 10);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  const checkDigit = (10 - (total % 10)) % 10;
  return checkDigit === parseInt(digits[9], 10);
}
